import * as Sentry from "@sentry/browser";
import type { AxiosError } from "axios";
import type { ReactNode } from "react";
import type { AlertType } from "~/components/icons";
import { ModalAction, ModalContent } from "~/components/Modal";

type ModalProps = {
	type: AlertType;
	title: string;
	children: ReactNode;
};
type OpenSnackBarFn = (message: { type: AlertType; text: string }) => void;
type OpenModalFn = (props: ModalProps) => void;
type CloseModalFn = () => void;

export const isNetworkError = (err: unknown): boolean => {
	// Axios network errors appear as "AxiosError Network Error"
	if (err && typeof err === "object" && "isAxiosError" in err) {
		const axiosErr = err as AxiosError;
		return axiosErr.code === "ERR_NETWORK";
	}
	// Fetch API network errors
	return err instanceof TypeError && err.message === "Failed to fetch";
};

export const handleUnexpectedError = (err: unknown): void => {
	console.error("Unexpected error:", err);
	Sentry.captureException(err);
};

export const createFlowErrorHandler = (
	openSnackBar: OpenSnackBarFn,
	openModal: OpenModalFn,
	closeModal: CloseModalFn,
) => {
	return (err: unknown): void => {
		console.error("Flow error:", err);
		if (isNetworkError(err)) {
			// Show snackbar for network errors (less intrusive)
			openSnackBar({
				type: "error",
				text: "Network error. Please check your connection and try again.",
			});
		} else {
			handleUnexpectedError(err);
			openModal({
				type: "error",
				title: "UNEXPECTED ERROR",
				children: (
					<>
						<ModalContent>
							<p className="text-center text-error">
								<span className="block font-semibold text-xl mb-4">
									UNEXPECTED ERROR
								</span>
								<span>An unexpected error occurred. Please try again.</span>
							</p>
						</ModalContent>
						<ModalAction>
							<button
								type="button"
								onClick={closeModal}
								className="btn btn-lg btn-t4-border rounded-full"
							>
								CLOSE
							</button>
						</ModalAction>
					</>
				),
			});
		}
	};
};
